import axiosInstance from "./api";
import { API_URL } from "./config";
import chatUiService, { CHAT_TYPE_PREFIX } from "./chatUiService";
import { getIdToken } from "./auth";

const BASE_URL = "/api/session";

/**
 * Create an ISO string with the timezone offset included and hours and minutes set to 0.
 * This is the format Python will interpret to include the timezone.
 *
 * Note: this will use _TODAY'S_ timezone, not the given date's timezone.
 * @param {Date} dateVal
 * @returns {string}
 */
const tzIsoFormat = (dateVal) => {
  const tz = new Date().getTimezoneOffset();
  const tz_hours = Math.abs(Math.floor(tz / 60));
  const tz_mins = Math.abs(tz % 60);
  const sign = tz > 0 ? '-' : '+';  // reverse the sign
  const Y = dateVal.getFullYear();
  const M = dateVal.getMonth() + 1;
  const D = dateVal.getDate();
  const h = dateVal.getHours();
  const m = dateVal.getMinutes();
  const s = dateVal.getSeconds();
  const ms = dateVal.getMilliseconds();
  const pad = (n) => String(n).padStart(2, '0')
  const pad3 = (n) => String(n).padStart(3, '0')
  return `${Y}-${pad(M)}-${pad(D)}T${pad(h)}:${pad(m)}:${pad(s)}.${pad3(ms)}${sign}${pad(tz_hours)}:${pad(tz_mins)}`
}


export const sessionService = {
  list: async (assignmentId = null) => {
    const params = assignmentId ? { assignment_id: assignmentId } : {};
    const response = await axiosInstance.get(`${BASE_URL}/list/`, { params });
    return response?.data?.data?.headers ?? [];
  },

  listEvents: async (sessionId) => {
    const params = { limit: "10000" };
    const response = await axiosInstance.get(
      `${BASE_URL}/${sessionId}/events`,
      { params },
    );
    if (!response?.data?.data) {
      return [];
    }

    return response.data.data.map((event) => {
      const { id, _score, session_id, ...filteredData } = event;
      return filteredData;
    });
  },

  getByAssignmentId: async (assignmentId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/assignment/${assignmentId}`,
    );
    return response?.data?.data ?? [];
  },

  getEntry: async (id) => {
    if (id.indexOf(CHAT_TYPE_PREFIX) === -1) {
      const response = await axiosInstance.get(`${BASE_URL}/id/${id}`);
      return response?.data?.data;
    } else {
      return chatUiService.getSessionForSessionUI(id);
    }
  },

  /**
   * Given an ambiguous ID that could be either a session ID or assignment ID, return the session ID.
   * @param {string} ambiguous_id - The ambiguous UUID.
   * @returns {Promise<?string>} - The session ID, or null if no IDs matched at all.
   */
  regularizeId: async (ambiguous_id) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/regularize-id/${ambiguous_id}`,
    );
    return response?.data?.data ?? null;
  },

  /**
   * Fetch session headers for a given date range and modality, returning an array of session entries.
   * @param {Date} start
   * @param {Date} end
   * @param {?string} modality
   * @param {?string} searchTerm
   * @param {string} order_by
   * @param {number} limit
   * @param {number} offset
  * @param {?string} status
   * @returns {Promise<*|*[]>}
   */
  getSessionHeaders: async (
    start,
    end,
    modality,
    searchTerm,
    order_by = "created_at:desc",
    limit,
    offset = 0,
    status = null,
  ) => {
    const params = {
      start: tzIsoFormat(start),
      end: tzIsoFormat(end),
      modality,
      search_term: searchTerm,
      offset,
      limit,
      order_by,
      status,
    };
    try {
      const sessionResponse = await axiosInstance.get(`${BASE_URL}/list`, {
        params,
      });

      return sessionResponse?.data?.data ?? [];
    } catch (error) {
      console.error("Error fetching headers:", error);
      throw error;
    }
  },

  /**
   * Fetch timeline data for a given date range and modality, returning an array of session entries with counts of events.
   * @param {Date} startTime
   * @param {Date} endTime
   * @param {?string} modality
   * @param {?string} searchTerm
   * @param {?string} order_by
  * @param {?string} status
   * @returns {Promise<{start_date: string, end_date: string, entries: []}>}
   */
  getTimeline: async (
    startTime,
    endTime,
    modality,
    searchTerm,
    order_by,
    status = null,
  ) => {
    const params = {
      start_time: tzIsoFormat(startTime),
      end_time: tzIsoFormat(endTime),
      modality: modality,
      search_term: searchTerm,
      order_by: order_by,
      status,
      timezone_offset: -new Date().getTimezoneOffset(),
    };

    try {
      const sessionResponse = await axiosInstance.get(`${BASE_URL}/timeline`, {
        params,
      });

      return sessionResponse?.data?.data;
    } catch (error) {
      console.error("Error fetching timeline data:", error);
      throw error; // or handle the error as appropriate for your application
    }
  },

  /**
   * Get the earliest date for which we have sessions.
   * @returns {Promise<Date|null>}
   */
  getEarliestDate: async () => {
    const params = {
      timezone_offset: -new Date().getTimezoneOffset(),
    };
    try {
      const response = await axiosInstance.get(`${BASE_URL}/earliest-date`, {
        params,
      });
      return response?.data ? new Date(response.data) : null;
    } catch (error) {
      console.error("Error fetching earliest date:", error);
    }
  },

  /**
   * Get header page bounds.
   * @param {Date} startTime
   * @param {Date} endTime
   * @param {?string} modality
   * @param {?string} searchTerm
   * @param {?string} order_by
   * @param {?number} page_size
  * @param {?string} status
   */
  getHeaderPageBounds: async (
    startTime,
    endTime,
    modality,
    searchTerm,
    order_by,
    page_size,
    status = null,
  ) => {
    const params = {
      start_time: tzIsoFormat(startTime),
      end_time: tzIsoFormat(endTime),
      modality: modality,
      search_term: searchTerm,
      order_by: order_by,
      page_size: page_size,
      status,
    };

    try {
      const response = await axiosInstance.get(`${BASE_URL}/list/pages`, {
        params,
      });

      return response?.data?.data;
    } catch (error) {
      console.error("Error fetching timeline data:", error);
      throw error; // or handle the error as appropriate for your application
    }
  },

  getTranscript: async (sessionId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/${sessionId}/transcript`,
    );
    return response?.data?.data ?? [];
  },

  getDiagnostics: async (sessionId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/${sessionId}/voice_bot_diagnostics`,
      { responseType: "blob" },
    );
    return response;
  },

  fetchRecordingUrl: async (sessionId) => {
    const params = new URLSearchParams({ token: await getIdToken() });
    const apiUrl = API_URL.replace(/\/+$/, ""); // Remove trailing slash if present
    return `${apiUrl}${BASE_URL}/fetch_recording/${sessionId}?${params.toString()}`;
  },
};

export default sessionService;
